import React, { useRef, useEffect, memo } from "react";
import { Container } from "@pixi/react";
import { useDimensions } from "../../../Contexts/DimensionsContext";
import { useReelRefs } from "../../../Contexts/ReelRefsContext";
import BackgroundDecorationController from "./BackgroundDecorationController";

/**
 * BackgroundDecoration
 *
 * Dedicated overlay layer for the tree/flowers `front_mask` animation from the
 * background_animation spine. Mounted inside UserInterface with zIndex 7 — above
 * GameContainer (z5) so it renders in front of the deer transition, but below
 * the toolbar buttons (z10).
 *
 * Uses the exact same positioning math as BackgroundAnimation for pixel-perfect
 * alignment with the background textures.
 *
 * Always visible — plays front_mask looping at all times.
 */
const BackgroundDecoration = () => {
  const controllerRef = useRef(null);
  const containerRef = useRef(null);
  const { dimensions, symbolWidth, symbolHeight, symbolScale, device, orientation } =
    useDimensions();
  const { backgroundDecorationControllerRef } = useReelRefs();

  // Initialize controller once
  useEffect(() => {
    if (containerRef.current && !controllerRef.current) {
      controllerRef.current = new BackgroundDecorationController(
        containerRef.current,
        dimensions,
        symbolWidth,
        symbolHeight,
        symbolScale,
        device,
        orientation,
      );

      // Store controller ref for external access (tension zoom)
      backgroundDecorationControllerRef.current = controllerRef.current;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle dimension changes
  useEffect(() => {
    if (controllerRef.current) {
      controllerRef.current.resize(
        dimensions,
        symbolWidth,
        symbolHeight,
        symbolScale,
        device,
        orientation,
      );
    }
  }, [dimensions, symbolWidth, symbolHeight, symbolScale, device, orientation]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (controllerRef.current) {
        controllerRef.current.cleanup();
      }
    };
  }, []);

  return (
    <Container
      name="background_decoration"
      ref={containerRef}
      zIndex={7}
      sortableChildren
    />
  );
};

export default memo(BackgroundDecoration);
